import type { ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card';
import { cn } from '@/lib/utils';
import { BloqueSeccion } from './help-blocks';
import {
  PARAM_SECCION,
  SECCIONES,
  seccionFrom,
  type SeccionId,
} from './help-content';
import {
  SeccionGuia,
  SeccionLeads,
  SeccionOrdenes,
  SeccionPantallas,
  SeccionPrimerosPasos,
  SeccionProblemas,
  SeccionRoles,
  SeccionWhatsApp,
} from './help-sections';

/**
 * Ayuda (`/help`, paso 8 del frontend) — la última pantalla del plan.
 *
 * Qué es: la guía de uso del panel, con las rutas reales. Qué NO es: no consume la API (es la única
 * pantalla del panel sin una sola consulta), no trae capturas ficticias ni cifras de ejemplo y no
 * repite los textos de otros sitios: las rutas y el orden de las pantallas salen de
 * `config/navigation.ts`, y las etiquetas y los colores de los estados, de `theme/status.ts`.
 *
 * Organización: un índice arriba (las ocho secciones) y un acordeón debajo. La sección abierta vive
 * en la URL (`?seccion=`), igual que el resto de las vistas del panel, así que un tema se puede
 * enlazar y compartir; si el valor no es una sección conocida, cae en la primera (ver `seccionFrom`).
 *
 * Es un acordeón y no pestañas con ruta propia a propósito: así la guía entera sigue viviendo en una
 * sola dirección (`/help`), que es como la alcanza el ítem del menú, y no hace falta inventar rutas
 * nuevas por cada tema.
 */

/** Contenido de cada sección (el índice de `SECCIONES` manda el orden y el título). */
const CONTENIDO: Record<SeccionId, ReactNode> = {
  'primeros-pasos': <SeccionPrimerosPasos />,
  pantallas: <SeccionPantallas />,
  whatsapp: <SeccionWhatsApp />,
  guia: <SeccionGuia />,
  ordenes: <SeccionOrdenes />,
  leads: <SeccionLeads />,
  roles: <SeccionRoles />,
  problemas: <SeccionProblemas />,
};

export function HelpPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const seccionAbierta = seccionFrom(searchParams);

  /** Abre una sección (y la lleva a la vista). El estado va a la URL, no a un `useState`. */
  function abrirSeccion(id: SeccionId) {
    const siguiente = new URLSearchParams(searchParams);
    siguiente.set(PARAM_SECCION, id);
    setSearchParams(siguiente);

    // `scrollIntoView` no existe en el render del servidor (el smoke monta esta pantalla sin
    // navegador), así que se comprueba antes de usarlo.
    if (typeof document !== 'undefined') {
      document.getElementById(id)?.scrollIntoView({ block: 'start' });
    }
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Guía de uso del panel</CardTitle>
          <CardDescription>
            Qué hace cada pantalla, cómo se conecta WhatsApp y qué significa cada estado de una orden
            o de un lead. Es contenido del propio panel: esta pantalla no consume la API.
          </CardDescription>
        </CardHeader>

        <CardContent className="space-y-3">
          <p className="text-caption text-ink-muted">
            Elige un tema. El que está abierto vive en la URL (`?seccion=…`), así que puedes
            compartir el enlace de un tema concreto.
          </p>

          <ul className="grid gap-3 sm:grid-cols-2">
            {SECCIONES.map((seccion) => {
              const Icon = seccion.icon;
              const activa = seccion.id === seccionAbierta;

              return (
                <li key={seccion.id}>
                  <button
                    type="button"
                    aria-current={activa ? 'true' : undefined}
                    onClick={() => abrirSeccion(seccion.id)}
                    className={cn(
                      'flex w-full items-start gap-3 rounded-panel border p-4 text-left transition-colors',
                      'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-500',
                      activa
                        ? 'border-brand-200 bg-brand-50/60'
                        : 'border-line bg-surface hover:border-brand-200 hover:bg-brand-50/60',
                    )}
                  >
                    <span className="flex size-8 shrink-0 items-center justify-center rounded-control bg-brand-50 text-brand-600">
                      <Icon size={16} />
                    </span>

                    <span className="min-w-0">
                      <span className="block text-body font-medium text-ink">{seccion.titulo}</span>
                      <span className="mt-0.5 block text-caption text-ink-muted">{seccion.bajada}</span>
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        </CardContent>
      </Card>

      {SECCIONES.map((seccion) => (
        <BloqueSeccion
          key={seccion.id}
          seccion={seccion}
          abierta={seccion.id === seccionAbierta}
          onToggle={abrirSeccion}
        >
          {CONTENIDO[seccion.id]}
        </BloqueSeccion>
      ))}

      <p className="text-caption text-ink-faint">
        Esta guía no consume la API: todo lo que dice sale de la documentación del proyecto y del
        código de las pantallas. Lo que todavía no existe se dice como pendiente.
      </p>
    </div>
  );
}